import type { ReactNode } from "react";
import type { IntegrationRun } from "../../../shared/types";
import { StatusChip } from "../common/StatusChip";
import { formatDateTime } from "../../utils/status";

interface MonitoringStat {
  label: string;
  value: ReactNode;
}

interface MonitoringPanelProps {
  className: string;
  eyebrow: string;
  title: string;
  icon: ReactNode;
  alertMessage?: string;
  alertIcon?: ReactNode;
  stats: MonitoringStat[];
  historyLabel?: string;
  historyTimestamp?: string;
  runs: IntegrationRun[];
  emptyMessage: string;
  onOpenJob: (jobId: string) => void;
}

function formatRunMeta(run: IntegrationRun): string {
  const source = run.workspaceName || run.sourceName;
  if (run.sourceType === "POWER_BI") {
    return `${source} - Last known refresh ${formatDateTime(run.actualStartTime || run.endTime || run.expectedStartTime)}`;
  }

  return `${source} - ${formatDateTime(run.lastCheckedAt)}`;
}

export function MonitoringPanel({
  className,
  eyebrow,
  title,
  icon,
  alertMessage,
  alertIcon,
  stats,
  historyLabel,
  historyTimestamp,
  runs,
  emptyMessage,
  onOpenJob
}: MonitoringPanelProps) {
  const panelClassName = [
    "panel",
    "monitoring-panel",
    alertMessage ? "monitoring-panel--alerting" : "",
    className
  ].filter(Boolean).join(" ");

  return (
    <section className={panelClassName}>
      <div className="panel-heading">
        <div>
          <p>{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        {icon}
      </div>

      {alertMessage && (
        <div className="synapse-error-banner">
          {alertIcon}
          <span>{alertMessage}</span>
        </div>
      )}

      <div className="synapse-stat-grid operational-stat-grid">
        {stats.map((stat) => (
          <div key={stat.label}>
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </div>
        ))}
      </div>

      {historyLabel && (
        <div className="event-history-heading">
          <span>{historyLabel}</span>
          <strong>{formatDateTime(historyTimestamp)}</strong>
        </div>
      )}

      {runs.length === 0 ? (
        <div className="empty-inline">{emptyMessage}</div>
      ) : (
        <div className="synapse-pipeline-list monitoring-panel__run-list">
          {runs.map((run) => (
            <button className="synapse-pipeline-row" key={run.id} type="button" onClick={() => onOpenJob(run.jobId)}>
              <span>
                <strong>{run.jobName || run.pipelineName}</strong>
                <small>{formatRunMeta(run)}</small>
              </span>
              <StatusChip status={run.status} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
